import { RJSFSchema, RJSFValidationError } from "@rjsf/utils";

/**
 * Resolves a JSON-Schema pointer (e.g. "#/properties/foo/properties/bar")
 * against a root schema and returns the sub-schema at that location, or
 * undefined when the path cannot be resolved.
 */
function resolveSchemaPath(schema: RJSFSchema, pointer: string): RJSFSchema | undefined {
  // Strip leading "#/" and split into segments; an empty pointer means root.
  const segments = pointer.replace(/^#\/?/, "").split("/").filter(Boolean);

  let node: unknown = schema;
  for (const segment of segments) {
    if (node === null || typeof node !== "object") return undefined;
    node = (node as Record<string, unknown>)[segment];
  }

  return node as RJSFSchema | undefined;
}

/**
 * Used as the `transformErrors` callback on BrostarForm.
 *
 * When AJV encounters a `Blob` value for a field typed `string` (which our
 * FileWidget stores at runtime), it emits a "must be string" type error.
 * We detect this by navigating the `schemaPath` up one level (stripping the
 * failing keyword) and checking whether that schema node declares
 * `format: "data-url"`. If so, the error is noise and is removed.
 */
export function filterDataUrlTypeErrors(
  errors: RJSFValidationError[],
  schema: RJSFSchema
): RJSFValidationError[] {
  return errors.filter((error) => {
    if (error.name !== "type" || !error.schemaPath) return true;

    // schemaPath ends with the failing keyword, e.g. "#/properties/foo/type".
    // Drop the last segment to get the pointer to the field's own schema node.
    const segments = error.schemaPath.split("/");
    const fieldPointer = segments.slice(0, -1).join("/");

    const fieldSchema = resolveSchemaPath(schema, fieldPointer);
    if (fieldSchema?.format === "data-url") return false;

    return true;
  });
}
